// Which explanation the "settlement not saved" dialog gives. The server
// (rentio-api-next SettlementService) refuses a confirm with a 409 when, in
// the meantime, another confirmed settlement took one of its costs or one of
// its provision months, or when another confirm of the same property is
// running at that very moment. Its English message tells those apart. A body
// over the API's limit (413) fails again on every retry, so it never asks to
// try again. A cost that changed since the wizard read it (paid, billed,
// edited or deleted in another tab) is a stale-costs refusal: the wizard
// reloads the costs and goes back to them (isStaleCostsRefusal). A wizard
// whose code predates the API's check on the costs' figures (a tab opened
// before an update) is told to reload the page. A departure settlement on
// the property confirmed or deleted since the wizard read the settlements is
// a departure refusal: the wizard reloads them (isDepartureChangedRefusal).
// A departure of a lease that already has a confirmed one, or whose end moved
// since the wizard read it, gets its own explanation; the first names the
// departure that is there already (getSaveFailedTextParams).

import {
  refusalSettlementRef,
  settlementRefName,
} from "../../../settlementName.utils";

const HTTP_CONFLICT = 409;
const HTTP_PAYLOAD_TOO_LARGE = 413;
const HTTP_UNPROCESSABLE = 422;

// The API's refusals of a confirm on stale costs (rentio-api-next
// settlementConfirm.utils, SETTLEMENT_CONFIRM_REFUSALS): their codes, and
// their messages for a body without a code.
const COSTS_CHANGED_CODE = "SETTLEMENT_COSTS_CHANGED";
const COSTS_CHANGED = /changed since it was opened/i;
const COSTS_GONE = /do not exist on this property/i;
const INVOICE_FIGURES_MISSING_CODE = "SETTLEMENT_INVOICE_FIGURES_MISSING";
const INVOICE_FIGURES_MISSING = /invoiceFigures must describe every cost/i;

// The API's refusals of a confirm that does not account for the confirmed
// departures of its costs (rentio-api-next settlementConfirm.utils,
// DEPARTURES_CHANGED_CODE): their code, and their messages for a body
// without one.
const DEPARTURES_CHANGED_CODE = "SETTLEMENT_DEPARTURES_CHANGED";
const DEPARTURES_CHANGED =
  /already settled part of these costs|departure settlement this settlement relies on no longer exists/i;

// The API's refusals of a departure the lease no longer allows
// (rentio-api-next settlementConfirm.utils): their codes, and their messages
// for a body without one.
const DEPARTURE_EXISTS_CODE = "SETTLEMENT_DEPARTURE_EXISTS";
const DEPARTURE_EXISTS = /^This lease already has departure settlement (.+)$/i;
const LEASE_END_CHANGED_CODE = "SETTLEMENT_LEASE_END_CHANGED";
const LEASE_END_CHANGED = /lease end changed since this departure/i;
// A departure that does not run to its lease's current last day (422,
// departurePeriodEnd): the lease end moved since the wizard read it (M11).
const DEPARTURE_PERIOD_END = /period ends on its lease's last day/i;
// A period that starts after today (422, periodStartsInFuture): the wizard
// read "today" before midnight, the API after it.
const PERIOD_STARTS_IN_FUTURE = /period cannot start after today/i;

// The API's refusal of a change to a confirmed settlement that would drop or
// redirect the link of a payment the tenant can still pay
// (rentio-api-next settlementUpdateById): in practice an invitation sent
// from another tab, which this view did not know.
const PAYMENT_STILL_OPEN = /payment is still open/i;
// rentio-api-next settlementUpdateById (N08, F07): a mark sent along with
// other tenants' actions, from a view older than what is stored; the link
// of a payment the tenant already paid.
const TENANT_MARK_ALONE = /mark is changed on its own/i;
const PAYMENT_ALREADY_PAID = /payment was already paid/i;

const PROVISION_MONTHS_TAKEN = /provision months/i;
const CONFIRM_RUNNING = /being confirmed right now/i;
const COSTS_TAKEN = /already part of confirmed/i;

interface IRequestError {
  status?: number;
  statusCode?: number;
  message?: string;
  response?: {
    status?: number;
    data?: {
      exception?: {
        message?: unknown;
        response?: { message?: unknown; code?: unknown; settlement?: unknown };
      };
    };
  };
}

interface IServerRefusal {
  status?: number;
  message: string;
  code?: string;
}

const readRefusal = (error: unknown): IServerRefusal => {
  const requestError = (error ?? {}) as IRequestError;
  const exception = requestError.response?.data?.exception;
  const message =
    exception?.response?.message ?? exception?.message ?? requestError.message;
  const code = exception?.response?.code;
  return {
    status:
      requestError.response?.status ??
      requestError.status ??
      requestError.statusCode,
    message: typeof message === "string" ? message : "",
    code: typeof code === "string" ? code : undefined,
  };
};

// Whether the server refused the confirm because a cost of the settlement
// changed since the wizard read it: deleted (422), or paid, billed directly
// or edited (409). The wizard then reloads its costs and shows what changed.
export const isStaleCostsRefusal = (error: unknown): boolean => {
  const { status, message, code } = readRefusal(error);
  if (status === HTTP_UNPROCESSABLE) return COSTS_GONE.test(message);
  if (status === HTTP_CONFLICT) {
    return code === COSTS_CHANGED_CODE || COSTS_CHANGED.test(message);
  }
  return false;
};

// Whether the server refused the confirm because a departure settlement on
// the property was confirmed or deleted since the wizard read the
// settlements. The wizard then reloads them, which recalculates the
// summary.
export const isDepartureChangedRefusal = (error: unknown): boolean => {
  const { status, message, code } = readRefusal(error);
  return (
    status === HTTP_CONFLICT &&
    (code === DEPARTURES_CHANGED_CODE || DEPARTURES_CHANGED.test(message))
  );
};

const isDepartureExistsRefusal = ({ status, message, code }: IServerRefusal) =>
  status === HTTP_CONFLICT &&
  (code === DEPARTURE_EXISTS_CODE || DEPARTURE_EXISTS.test(message));

// The departure a "this lease already has a departure" refusal names, as the
// API wrote it: its name without the quotes, else its id.
const existingDepartureName = (message: string): string => {
  const named = DEPARTURE_EXISTS.exec(message)?.[1]?.trim() ?? "";
  return named.replace(/^"(.*)"$/, "$1");
};

export const getSaveFailedTextKey = (error: unknown): string => {
  const refusal = readRefusal(error);
  const { status, message, code } = refusal;
  if (status === HTTP_PAYLOAD_TOO_LARGE) {
    return "settlement.save_failed.too_large";
  }
  if (isDepartureExistsRefusal(refusal)) {
    return "settlement.save_failed.departure_exists";
  }
  if (
    status === HTTP_CONFLICT &&
    (code === LEASE_END_CHANGED_CODE || LEASE_END_CHANGED.test(message))
  ) {
    return "settlement.save_failed.lease_end_changed";
  }
  if (status === HTTP_UNPROCESSABLE && DEPARTURE_PERIOD_END.test(message)) {
    return "settlement.save_failed.lease_end_changed";
  }
  if (status === HTTP_UNPROCESSABLE && PERIOD_STARTS_IN_FUTURE.test(message)) {
    return "settlement.period.error.starts_in_future";
  }
  if (
    status === HTTP_UNPROCESSABLE &&
    (code === INVOICE_FIGURES_MISSING_CODE ||
      INVOICE_FIGURES_MISSING.test(message))
  ) {
    return "settlement.save_failed.reload_page";
  }
  if (isStaleCostsRefusal(error)) {
    return "settlement.save_failed.costs_changed";
  }
  if (isDepartureChangedRefusal(error)) {
    return "settlement.save_failed.departure_changed";
  }
  if (status !== HTTP_CONFLICT && !COSTS_TAKEN.test(message)) {
    return "settlement.save_failed.info";
  }
  if (PROVISION_MONTHS_TAKEN.test(message)) {
    return "settlement.save_failed.provisions_already_settled";
  }
  // Nothing was taken, the other confirm was simply still running: trying
  // again is all it takes.
  if (CONFIRM_RUNNING.test(message)) {
    return "settlement.save_failed.info";
  }
  return "settlement.save_failed.already_settled";
};

// The values the text of getSaveFailedTextKey fills in: the name of the
// departure that is there already, in the reader's language when the
// refusal sends it (refusalSettlementRef), else as its message names it.
export const getSaveFailedTextParams = (
  error: unknown,
): Record<string, string> | undefined => {
  const refusal = readRefusal(error);
  if (!isDepartureExistsRefusal(refusal)) return undefined;
  const ref = refusalSettlementRef(
    (error as IRequestError | undefined)?.response?.data?.exception?.response
      ?.settlement,
  );
  return {
    name: ref ? settlementRefName(ref) : existingDepartureName(refusal.message),
  };
};

// Whether the server refused a change to a confirmed settlement because a
// payment of one of its tenants is still open that the change would drop:
// the view was older than what is stored.
export const isPaymentStillOpenRefusal = (error: unknown): boolean => {
  const { status, message } = readRefusal(error);
  return status === HTTP_CONFLICT && PAYMENT_STILL_OPEN.test(message);
};

// Which explanation a failed change to a confirmed settlement gets (a
// payment link, a manual paid mark): a payment still open that this view
// did not know, else the general "try again".
export const getConfirmedWriteFailedTextKey = (error: unknown): string => {
  if (isPaymentStillOpenRefusal(error)) return "settlement.payment_still_open";
  const { status, message } = readRefusal(error);
  if (status === HTTP_CONFLICT && PAYMENT_ALREADY_PAID.test(message)) {
    return "settlement.confirmed.payment_already_paid";
  }
  if (status === HTTP_CONFLICT && TENANT_MARK_ALONE.test(message)) {
    return "settlement.confirmed.changed_meanwhile";
  }
  return "settlement.save_failed.info";
};
